import { useContext } from 'react'

import {
  Box, Button, ModalContext, SidebarModal, Text,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'
import renderPortalStatusText from '@functions/portal'

import AgencyPortalSelect from './components/AgencyPortalSelect'
import MoreOptions from './components/MoreOptions'
import PortalOptions from './components/PortalOptions'
import useCampaignPortalModal from './hooks/useCampaignPortalModal'

const MODAL_KEY = 'EditCampaignPortalModal'

type ModalCallbacks = {
  closeModal: () => void,
  updateAgency: () => void,
  updateCampaignPortal: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const EditCampaignPortalModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, updateAgency, updateCampaignPortal } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { campaignPortal: initCampaignPortal } = modalPayload

  const campaignPortalModalPayload = useCampaignPortalModal(initCampaignPortal)

  const {
    campaignPortalFormPayload,
    callbacks: {
      updateCampaignPortal: updateFn,
    },
    hasFilteredPortalOptions,
    isSyncing,
    lastSyncDateObj,
    lastSyncFailed,
    portal,
    updatingCampaignPortal,
  } = campaignPortalModalPayload

  const {
    entityState,
  } = campaignPortalFormPayload

  const updateCampaignPortalPayload = {
    callbacks: {
      action: updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: 'Campaign Portal Updated',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Portal Sync" titleSecondLine="Settings" />

      <SidebarModal.SubHeader>
        {/* @ts-expect-error TS2322 */}
        <Box boxProps={{ lineHeight: 1.3 }} flexDirection="column">
          {/*
           // @ts-expect-error TS2339 */}
          <Text>{portal.title}</Text>

          <Text color="bodyFontLightColor" fontSize="xsmall" marginTop="medium">
            {renderPortalStatusText({ isSyncing, lastSyncDateObj, lastSyncFailed })}
          </Text>
        </Box>
      </SidebarModal.SubHeader>

      <SidebarModal.Content hasSubHeader>
        <AgencyPortalSelect
          // @ts-expect-error TS2339
          portalId={portal.id}
          campaignPortalModalPayload={campaignPortalModalPayload}
        />

        <PortalOptions
          campaignPortalModalPayload={campaignPortalModalPayload}
        />

        {!!hasFilteredPortalOptions && (
          <MoreOptions
            campaignPortalModalPayload={campaignPortalModalPayload}
            callbacks={{ updateAgency, closeModal }}
          />
        )}
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          loading={updatingCampaignPortal}
          onClick={() => updateCampaignPortal(updateCampaignPortalPayload)}
          size="large"
        >
          Save
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <EditCampaignPortalModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
